import RequireLogin from '@/app/RequireLogin';
import RequireRole from '@/app/RequireRole';
import AppRouter from '@/AppRouter';
import { ContentErrorBoundary } from '@/components/ContentErrorBoundary';
import { SuspenseTrap } from '@/components/SuspenseTrap';
import { Toaster } from '@/components/ui/sonner';
import logAppConfig from '@/util/logAppConfig';
import { refreshSnapshotsAtom } from '@rock/db-admin';
import { Provider as JotaiProvider, useSetAtom } from 'jotai';

import '@xyflow/react/dist/base.css';
import { type FC, type PropsWithChildren, useEffect } from 'react';
import AuthInitializer from './app/AuthInitializer';
import { TooltipProvider } from './components/ui/tooltip';

const App = () => {
    useEffect(logAppConfig, []);
    return (
        <JotaiProvider>
            <TooltipProvider>
                <AuthInitializer>
                    <RequireLogin>
                        <RequireRole>
                            <RootBoundary>
                                <AppRouter />
                            </RootBoundary>
                        </RequireRole>
                        <Toaster position="top-center" theme="light" />
                    </RequireLogin>
                </AuthInitializer>
            </TooltipProvider>
        </JotaiProvider>
    );
};

/**
 * Backstop for anything that escapes AdminLayout's content-pane boundary (e.g. a LeftNav atom
 * error) — without it, an uncaught render error unmounts the whole React root to a white screen.
 */
const RootBoundary: FC<PropsWithChildren> = ({ children }) => {
    const refreshSnapshots = useSetAtom(refreshSnapshotsAtom);
    return (
        <ContentErrorBoundary onRetry={refreshSnapshots}>
            <SuspenseTrap boundary="app-root">{children}</SuspenseTrap>
        </ContentErrorBoundary>
    );
};

export default App;
